import Phaser from 'phaser';
import { GameService } from '../../../../services/gameService';
import gamePopupService from '../../../../services/gamePopupService';

// Define game states
type GameState = 'ready' | 'playing' | 'gameOver';

export class CloudTowerScene extends Phaser.Scene {
  private character?: Phaser.Physics.Arcade.Sprite;
  private clouds!: Phaser.Physics.Arcade.Group;
  private background?: Phaser.GameObjects.Image;
  private tower?: Phaser.GameObjects.Image;
  private towerTop?: Phaser.Physics.Arcade.Sprite;
  
  private gameState: GameState = 'ready';
  private score = 0;
  private scoreText?: Phaser.GameObjects.Text;
  private highscoreText?: Phaser.GameObjects.Text;
  private playButton?: Phaser.GameObjects.Image;
  private gameOverText?: Phaser.GameObjects.Text;
  private gameOverPopup?: Phaser.GameObjects.Image;
  private gameOverOverlay?: Phaser.GameObjects.Rectangle;
  private finalScoreText?: Phaser.GameObjects.Text;
  private playAgainText?: Phaser.GameObjects.Text;
  private playAgainButton?: Phaser.GameObjects.Image;

  private nextCloudSide: 'left' | 'right' = 'left';
  private cloudSpeed = 450; // Reduced from 300 to make clouds fly slower
  private lastCloud?: Phaser.Physics.Arcade.Sprite;
  private scoredClouds: Set<Phaser.Physics.Arcade.Sprite> = new Set();
  private isCharacterPushed = false; // Track if character was pushed by cloud
  private justStartedGame = false; // Track if game just started to prevent immediate jump
  private isApiCallInProgress = false; // Track API call status
  private isLandingProcessed = false; // Track if landing has been processed to prevent multiple spawns

  private readonly CLOUD_SPACING = 200;
  private readonly CLOUD_SPAWN_DELAY = 300; // 1.5 seconds delay between cloud spawns

  constructor() {
    super({ key: 'CloudTowerScene' });
  }

  preload(): void {
    // Assets are already loaded by LoadingScene
    // No need to load again here
  }

  create(): void {
    // Set background color
    this.cameras.main.setBackgroundColor('#083e96');
    
    // Set camera bounds to allow vertical movement
    this.cameras.main.setBounds(0, -10000, 1080, 12000);
    
    // Create background elements
    this.background = this.add.image(540, 960, 'sky').setDisplaySize(1080, 1920).setAlpha(0.3);
    this.tower = this.add.image(540, 1920, 'tower').setOrigin(0.5, 1);
    const towerWidth = this.tower.displayWidth;
    const towerHeight = this.tower.displayHeight;
    const ratio = towerWidth / 1080;
    this.tower.setDisplaySize(1080, towerHeight / ratio);

    // Initialize physics
    this.physics.world.setBounds(0, 0, 1080, 1920);
    this.physics.world.setBoundsCollision(true, true, false, true);

    // Create clouds group
    this.clouds = this.physics.add.group({
      allowGravity: false,
      immovable: true,
    });

    // Add physics body to tower top for character to stand on
    const towerTopY = this.tower.getTopCenter().y;
    this.towerTop = this.physics.add.staticSprite(540, towerTopY, 'cloud');
    this.towerTop.setDisplaySize(200, 5).setAlpha(0).refreshBody();

    // Create UI elements
    this.scoreText = this.add.text(540, 100, 'Score: 0', { 
      fontSize: '60px', 
      color: '#FFFFFF', 
      fontFamily: 'gilroy-medium' 
    }).setOrigin(0.5).setScrollFactor(0).setVisible(false);
    
    this.highscoreText = this.add.text(50, 50, `High Score: 0`, { 
      fontSize: '36px', 
      color: '#FFFFFF',
      fontFamily: 'gilroy-medium'
    }).setOrigin(0, 0).setScrollFactor(0);
    this.playButton = this.add.image(540, 580, 'playButton').setInteractive().on('pointerdown', this.startGame, this);
    
    // Create dark overlay for game over screen
    this.gameOverOverlay = this.add.rectangle(540, 960, 1080, 1920, 0x000000, 0.7).setScrollFactor(0).setVisible(false).setDepth(999);
    
    // Create game over popup - remove interactive to prevent clicking anywhere to restart
    this.gameOverPopup = this.add.image(540, 960, 'gameover').setOrigin(0.5).setScrollFactor(0).setVisible(false).setDepth(1000);
    
    // Game over text on the popup
    this.gameOverText = this.add.text(540, 800, 'Game Over\nClick to Play Again', { 
      fontSize: '64px', 
      color: '#FFFFFF', 
      align: 'center', 
      fontFamily: 'gilroy-medium'
    }).setOrigin(0.5).setScrollFactor(0).setVisible(false).setDepth(1001);
    
    // Final score text on the popup
    this.finalScoreText = this.add.text(540, 1050, 'Số điểm: 0', { 
      fontSize: '48px', 
      color: '#FFFFFF', 
      fontFamily: 'gilroy-medium'
    }).setOrigin(0.5).setScrollFactor(0).setVisible(false).setDepth(1001);

    // Play again instruction text
    this.playAgainText = this.add.text(540, 1280, 'Chạm để chơi lại', { 
      fontSize: '32px', 
      color: '#FFFFFF', 
      fontFamily: 'gilroy-medium'
    }).setOrigin(0.5).setScrollFactor(0).setVisible(false).setDepth(1001).setAlpha(0.7);

    // Listen for input to trigger the jump
    this.input.on('pointerdown', this.handleJump, this);
    
    this.showStartScreen();
  }

  showStartScreen(): void {
    this.gameState = 'ready';
    this.playButton?.setVisible(true);
    
    // Get and display high score in top-left corner
    const currentHighscore = parseInt(localStorage.getItem('cloudTowerHighscore') || '0', 10);
    this.highscoreText?.setText(`High Score: ${currentHighscore}`).setVisible(true);
    
    this.scoreText?.setVisible(false);
    this.gameOverText?.setVisible(false);
    this.gameOverPopup?.setVisible(false);
    this.gameOverOverlay?.setVisible(false);
    this.finalScoreText?.setVisible(false);
    this.playAgainText?.setVisible(false);
    this.playAgainButton?.setVisible(false);
    
    // Create Kiloba character at tower top
    if (this.character) this.character.destroy();
    const towerTopY = this.tower!.getTopCenter().y;
    this.character = this.physics.add.sprite(540, towerTopY - 10, 'character'); // Position slightly above tower
    this.character.setOrigin(0.5, 1); // Set origin to bottom-center
    this.character.setGravityY(1500); // Apply gravity so character falls onto tower
    this.character.setCollideWorldBounds(true);
    (this.character.body as Phaser.Physics.Arcade.Body).setSize(this.character.width * 0.5, this.character.height * 0.9);
    
    // Add collision with tower top so character can stand on it
    this.physics.add.collider(this.character, this.towerTop!);
    
    this.clouds.clear(true, true);
  }

  startGame(): void {
    this.gameState = 'playing';
    this.score = 0;
    this.cloudSpeed = 450; // Match the initial cloudSpeed value
    this.scoredClouds.clear(); // Reset scored clouds
    this.isCharacterPushed = false; // Reset character state
    this.justStartedGame = true; // Prevent immediate jump after starting
    this.isLandingProcessed = false; // Reset landing flag
    this.scoreText?.setText('0').setVisible(true);
    this.playButton?.setVisible(false);
    this.highscoreText?.setVisible(false);
    this.gameOverText?.setVisible(false);
    this.gameOverPopup?.setVisible(false);
    this.gameOverOverlay?.setVisible(false);
    this.finalScoreText?.setVisible(false);
    this.playAgainText?.setVisible(false);
    this.playAgainButton?.setVisible(false);

    // Activate physics for existing character
    if (this.character) {
      this.character.setGravityY(1500); // Enable gravity when game starts
      (this.character.body as Phaser.Physics.Arcade.Body).onWorldBounds = true;
    }

    // No start cloud needed - character starts directly on tower
    this.lastCloud = undefined;

    // Add collision with callback to detect landing (only if character exists)
    if (this.character) {
      this.physics.add.collider(this.character, this.clouds, (char, cloud) => {
        const charSprite = char as Phaser.Physics.Arcade.Sprite;
        const cloudSprite = cloud as Phaser.Physics.Arcade.Sprite;
        const charBody = charSprite.body as Phaser.Physics.Arcade.Body;
        
        // Check for side collision (horizontal collision while cloud is moving) - GAME OVER!
        if (cloudSprite.body && cloudSprite.body.velocity.x !== 0) {
          // If character is colliding with moving cloud from the side, end game immediately
          if (charBody.touching.left || charBody.touching.right) {
            console.log('Side collision with moving cloud! Game Over!');
            this.endGame();
            return; // Exit early to prevent further processing
          }
          
          // Stop the cloud if character is touching it from top/bottom
          cloudSprite.setVelocityX(0);
          cloudSprite.setAccelerationX(0); // Stop acceleration when cloud stops
          console.log('Cloud stopped due to character collision');
        }
        
        // Check if character is landing on top of cloud
        if (charBody.touching.down && charBody.velocity.y >= 0 && !this.isLandingProcessed) {
          console.log('Character landed on cloud via collision!'); // Debug log
          
          // Set flag to prevent multiple processing in same landing
          this.isLandingProcessed = true;
          
          // Reset character rotation and effects when landing safely
          charSprite.setAngularVelocity(0);
          charSprite.setRotation(0);
          
          // Also reset physics body rotation
          charBody.angularVelocity = 0;
          charBody.rotation = 0;
          
          charSprite.setTint(0xffffff); // Reset tint to normal
          this.isCharacterPushed = false;
          
          // Check if this cloud hasn't been scored yet for points
          const shouldScore = !this.scoredClouds.has(cloudSprite);
          if (shouldScore) {
            this.scoredClouds.add(cloudSprite); // Mark this cloud as scored for points
          }
          
          this.onSuccessfulJump(cloudSprite, shouldScore);
        }
      });
      this.physics.add.collider(this.character, this.towerTop!);
      
      // Camera follows the character
      this.cameras.main.startFollow(this.character, true, 0.08, 0.08);
      this.cameras.main.setFollowOffset(0, -400);
    }

    // Spawn first cloud to start the game
    this.spawnCloud();
    
    // Reset the justStartedGame flag after a short delay to allow jumping
    this.time.delayedCall(200, () => {
      this.justStartedGame = false;
    });
  }

  spawnCloud(): void {
    if (this.gameState !== 'playing') return;

    let y;
    if (this.lastCloud) {
      // Next cloud at same bottom level as the last cloud + spacing
      y = this.lastCloud.y - this.CLOUD_SPACING;
    } else {
      // First cloud spawns at same bottom level as character + spacing
      const characterBottomY = this.character ? this.character.y : this.tower!.getTopCenter().y;
      y = characterBottomY;
    }
    
    this.spawnCloudAtHeight(y);
  }

  spawnCloudAtHeight(y: number): void {
    if (this.gameState !== 'playing') return;
    
    let x;
    let velocityX;

    if (this.nextCloudSide === 'left') {
      x = -150; // Start off-screen left
      velocityX = this.cloudSpeed;
      this.nextCloudSide = 'right';
    } else {
      x = 1230; // Start off-screen right
      velocityX = -this.cloudSpeed;
      this.nextCloudSide = 'left';
    }

    const newCloud = this.clouds.create(x, y, 'cloud') as Phaser.Physics.Arcade.Sprite;
    const characterHeight = this.character ? this.character.displayHeight : 100;
    newCloud.setDisplaySize(300, characterHeight);
    newCloud.setOrigin(0.5, 1); // Set origin to bottom-center like character
    newCloud.refreshBody();
    
    // Adjust physics body to be shorter so character appears to stand "in" the cloud
    const cloudBody = newCloud.body as Phaser.Physics.Arcade.Body;
    cloudBody.setSize(newCloud.width, newCloud.height * 0.8); // Reduce height to 80% of display size
    cloudBody.setOffset(0, newCloud.height * 0.2); // Offset to align with bottom

    newCloud.setVelocityX(velocityX);
    
    // Add acceleration to make clouds speed up as they enter screen
    const accelerationX = velocityX > 0 ? 75 : -75; // Positive acceleration for left-to-right, negative for right-to-left
    newCloud.setAccelerationX(accelerationX);

    this.lastCloud = newCloud;
  }

  handleJump(): void {
    if (this.gameState !== 'playing' || !this.character || this.justStartedGame) return;
    
    const body = this.character.body as Phaser.Physics.Arcade.Body;
    // Only jump if standing on a cloud or tower
    if (!body.touching.down) return;

    // Reset landing flag when jumping - allows processing next landing
    this.isLandingProcessed = false;
    
    this.character.setVelocityY(-1200); // Jump!
  }

  onSuccessfulJump(landedCloud: Phaser.Physics.Arcade.Sprite, shouldScore: boolean = true): void {
    if (!this.character) return;

    // Only add score if this cloud hasn't been scored yet
    if (shouldScore) {
      // Check for perfect jump
      const perfectJumpThreshold = 50;
      const distance = Phaser.Math.Distance.Between(this.character.x, 0, landedCloud.x, 0);

      if (distance < perfectJumpThreshold) {
        this.score += 2; // Perfect jump bonus
      } else {
        this.score += 1;
      }

      this.scoreText?.setText(`${this.score}`);
      
      // Increase difficulty only when scoring
      this.cloudSpeed += 20;
    }

    // Always spawn next cloud regardless of scoring
    this.time.delayedCall(this.CLOUD_SPAWN_DELAY, () => {
      if (shouldScore) {
        // Normal progression - spawn at new height based on lastCloud
        this.spawnCloud();
      } else {
        // Missed the last cloud, spawn replacement at same height as the missed cloud
        const missedCloudHeight = this.lastCloud ? this.lastCloud.y : landedCloud.y - this.CLOUD_SPACING;
        this.spawnCloudAtHeight(missedCloudHeight);
      }
    });
  }
  
  async endGame(): Promise<void> {
    if (this.gameState === 'gameOver') return;
    this.gameState = 'gameOver';

    console.log('Game Over! Final Score:', this.score);

    // Update high score
    const currentHighscore = parseInt(localStorage.getItem('cloudTowerHighscore') || '0', 10);
    if (this.score > currentHighscore) {
      localStorage.setItem('cloudTowerHighscore', this.score.toString());
    }

    // Show popup with final score using left-right layout
    this.gameOverOverlay?.setVisible(true);
    this.gameOverPopup?.setVisible(true);
    
    // Create score display with left-right layout similar to energy
    const scoreFontSize = 48;
    const scaledWidth = 400;
    const leftMargin = 540 - scaledWidth * 0.7;
    const rightMargin = 540 + scaledWidth * 0.7;
    
    // Score label (left aligned)
    const scoreLabel = this.add.text(leftMargin, 1050, 'Số điểm:', {
      fontSize: `${scoreFontSize}px`,
      color: '#FFFFFF',
      fontFamily: 'gilroy-medium',
      resolution: window.devicePixelRatio || 1
    }).setOrigin(0, 0.5).setScrollFactor(0).setDepth(1001);
    
    // Score value (right aligned)
    const scoreValue = this.add.text(rightMargin, 1050, `${this.score}`, {
      fontSize: `${scoreFontSize}px`,
      color: '#FFFFFF',
      fontFamily: 'gilroy-medium',
      resolution: window.devicePixelRatio || 1
    }).setOrigin(1, 0.5).setScrollFactor(0).setDepth(1001);
    
    // Add loading text for API call
    const loadingText = this.add.text(540, 1100, 'Đang gửi điểm...', { 
      fontSize: '28px', 
      color: '#FFFF00', 
      fontFamily: 'gilroy-medium'
    }).setOrigin(0.5).setScrollFactor(0).setDepth(1002);

    // Submit score to API
    if (!this.isApiCallInProgress) {
      this.isApiCallInProgress = true;
      
      try {
        const result = await GameService.complete9CloudsGame(this.score);

        console.log('API call completed with result:', result);
        
        // Hide loading text
        loadingText.setVisible(false);
        
        if (result) {
          
          // Show energy earned info with left-right layout similar to racing game
          const energyFontSize = 44;
          const scaledWidth = 400; // Similar to popup width
          const leftMargin = 540 - scaledWidth * 0.7;
          const rightMargin = 540 + scaledWidth * 0.7;
          
          // Energy label (left aligned)
          const energyLabel = this.add.text(leftMargin, 1120, 'Năng lượng:', {
            fontSize: `${energyFontSize}px`,
            color: '#fff',
            fontFamily: 'gilroy-medium',
            resolution: window.devicePixelRatio || 1
          }).setOrigin(0, 0.5).setScrollFactor(0).setDepth(1002);
          
          // Energy value (right aligned)
          const energyValue = this.add.text(rightMargin, 1120, `+${result.energyEarned}`, {
            fontSize: `${energyFontSize}px`,
            color: '#fff',
            fontFamily: 'gilroy-medium',
            resolution: window.devicePixelRatio || 1
          }).setOrigin(1, 0.5).setScrollFactor(0).setDepth(1002);
          
          // Animate energy reward for both label and value
          if (result.energyEarned > 0) {
            this.tweens.add({
              targets: [energyLabel, energyValue],
              scaleX: 1.2,
              scaleY: 1.2,
              duration: 300,
              yoyo: true,
              repeat: 2,
              ease: 'Power2'
            });
          }
          
          // Show remaining attempts info if available
          if (typeof result.remainingAttempts === 'number') {
            const remainingFontSize = 32;
            const remainingText = this.add.text(540, 1200, 
              `Lượt chơi còn lại hôm nay: ${result.remainingAttempts}`, {
              fontSize: `${remainingFontSize}px`,
              color: '#cccccc',
              fontFamily: 'gilroy-medium',
              stroke: '#000000',
              strokeThickness: 1,
              resolution: window.devicePixelRatio || 1
            }).setOrigin(0.5).setScrollFactor(0).setDepth(1002);

            // Show encouragement popup
            const { remainingAttempts } = result;
            if (remainingAttempts > 0) {
              let message = '';
              if (remainingAttempts === 1) {
                message = "Chốt hạ nào! Lượt cuối cùng hôm nay, không thử thì tiếc lắm đó 😉";
              } else {
                message = `Hay quá! Nhưng còn ${remainingAttempts} lượt nhận thưởng nữa kìa, thử tiếp nhé?`;
              }

              // Use a timeout to show the popup slightly after the game over screen appears
              this.time.delayedCall(500, () => {
                gamePopupService.showPopup({
                  type: 'info',
                  title: 'Còn lượt chơi!',
                  message: message,
                  actionText: 'OK',
                  onAction: () => {}, // Empty action, just closes the popup
                  dismissible: true,
                });
              });
            }
          }
        } else {
          // Show error message
          loadingText.setText('Lỗi kết nối! Thử lại sau.');
          loadingText.setColor('#FF0000');
          loadingText.setVisible(true);
        }
      } catch (error) {
        console.error('Error submitting 9 clouds game score:', error);
        loadingText.setText('Lỗi kết nối! Thử lại sau.');
        loadingText.setColor('#FF0000');
      } finally {
        this.isApiCallInProgress = false;
      }
    }
    
    // Show play again button with background image
    const playAgainFontSize = 32;
    
    // Create button background image
    const buttonBgTexture = this.textures.exists('bg_button') ? 'bg_button' : null;
    
    if (buttonBgTexture) {
      // Use the bg_button.png as background - moved down to 1350
      this.playAgainButton = this.add.image(540, 1350, 'bg_button');
      
      // Scale the button to appropriate size (adjust as needed based on your image dimensions)
      const buttonScale = 0.8; // Adjust this scale factor as needed
      this.playAgainButton.setScale(buttonScale);
      this.playAgainButton.setAlpha(0.9);
      this.playAgainButton.setScrollFactor(0);
      this.playAgainButton.setDepth(1003);
      this.playAgainButton.setVisible(true);
      
      // Add text on top of the button - moved down to 1350
      this.playAgainText = this.add.text(540, 1350, 'Chạm để chơi lại', {
        fontSize: `${playAgainFontSize}px`,
        color: '#ffffff',
        fontFamily: 'gilroy-medium',
        stroke: '#000000',
        strokeThickness: 1,
        resolution: window.devicePixelRatio || 1
      }).setOrigin(0.5).setScrollFactor(0).setDepth(1004).setVisible(true);
      
      // Make both button and text interactive
      this.playAgainButton.setInteractive();
      this.playAgainButton.on('pointerdown', () => {
        this.scene.restart(); // Use scene restart instead of startGame to properly reset everything
      });
      
      // Make text also interactive to ensure button works properly
      this.playAgainText.setInteractive();
      this.playAgainText.on('pointerdown', () => {
        this.scene.restart(); // Use scene restart instead of startGame to properly reset everything
      });
      
      // Make both button and text blink together
      this.tweens.add({
        targets: [this.playAgainButton, this.playAgainText],
        alpha: 0.5,
        duration: 1000,
        yoyo: true,
        repeat: -1
      });
    } else {
      // Fallback to text-only if image not available - moved down to 1350
      this.playAgainText = this.add.text(540, 1350, 'Chạm để chơi lại', {
        fontSize: `${playAgainFontSize}px`,
        color: '#ffffff',
        fontFamily: 'gilroy-medium',
        stroke: '#000000',
        strokeThickness: 1,
        resolution: window.devicePixelRatio || 1
      }).setOrigin(0.5).setScrollFactor(0).setDepth(1004).setVisible(true);
      
      this.playAgainText.setInteractive();
      this.playAgainText.on('pointerdown', () => {
        this.scene.restart(); // Use scene restart instead of startGame to properly reset everything
      });
      
      // Make text blink
      this.tweens.add({
        targets: this.playAgainText,
        alpha: 0.3,
        duration: 1000,
        yoyo: true,
        repeat: -1
      });
    }
    
    // Remove the general click handler on the popup - now only the button works
    // this.gameOverPopup?.setInteractive().once('pointerdown', () => {
    //   this.startGame();
    // });
  }

  update(): void {
    if (this.gameState !== 'playing' || !this.character) return;

    // Game over if character falls below tower top
    const towerTopY = this.tower!.getTopCenter().y;
    if (this.character.y > towerTopY + 100) { // Add small buffer
      this.endGame();
    }

    // Stop clouds when they reach the center of the screen OR when character is on them
    this.clouds.children.each(c => {
      const cloud = c as Phaser.Physics.Arcade.Sprite;
      if (cloud && cloud.body) {
        const centerX = 540; // Center of 1080px screen
        const tolerance = 5; // How close to center before stopping
        
        let shouldStopCloud = false;
        
        // Check if character is standing on this cloud
        if (this.character) {
          const charBody = this.character.body as Phaser.Physics.Arcade.Body;
          
          // Check if character is standing on top of this cloud
          this.physics.overlap(this.character, cloud, () => {
            if (charBody.touching.down && charBody.velocity.y >= 0) {
              // Character is standing on cloud - stop the cloud
              shouldStopCloud = true;
              console.log('Character standing on cloud, stopping it');
            } else if (cloud.body && cloud.body.velocity.x !== 0) {
              // Character is colliding with moving cloud - GAME OVER!
              shouldStopCloud = true;
              console.log('Cloud collision! Game Over!');
              this.endGame();
              return; // Exit early to prevent further processing
            }
          });
        }
        
        // Also stop if cloud reached center and no character interaction
        if (!shouldStopCloud && Math.abs(cloud.x - centerX) <= tolerance && cloud.body.velocity.x !== 0) {
          shouldStopCloud = true;
        }
        
        // Stop the cloud if any condition is met
        if (shouldStopCloud && cloud.body.velocity.x !== 0) {
          cloud.setVelocityX(0);
          cloud.setAccelerationX(0); // Stop acceleration when cloud stops
          // Only snap to center if not stopped by character interaction
          if (Math.abs(cloud.x - centerX) <= tolerance) {
            cloud.x = centerX;
          }
        }
      }
      return true;
    });

  }
}
